import React, { useState, useEffect, useRef } from 'react'
import Editor from 'components/editor'
import { IContextMenuState, ITableViewColumn, ICellValue, EditorContent, IUpdateTableViewCell } from 'types'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { INITIAL_SELECTED_CELL, DEFAULT_FORM_COLUMN_WIDTH } from 'components/spreadsheet/constants/const'
import { Option, Lock, Join, Info } from 'components/icons'
import ColumnMenu from 'components/spreadsheet/components/menu/views/column'
import Cell from 'components/spreadsheet/components/cell'
import { updateView, refreshSelectOptions } from 'components/spreadsheet/contexts/data/actions'
import Button from 'components/button'
import { useApplicationStore } from 'hooks/application'
import { isFieldValueEmpty, isEditorEmpty, isColumnLockedByRules } from 'components/spreadsheet/helpers/functions'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { useProject } from 'hooks/useProject'
import DeleteColumnModal from 'components/spreadsheet/components/modal/views/deletecolumn'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import EditColumnMenu from '../menu/views/editcolumn'
import FormCellMenu from '../menu/views/formcell'
import { hashString } from 'helpers/utils'
import { getPlainTextDescription } from 'components/spreadsheet/helpers/format'
import { useThemedColours } from 'hooks/useTheme'

const createFormValueState = (values: Record<string, ICellValue>, columns: ITableViewColumn[]) => {
  const newValues: Record<string, ICellValue> = {}
  for (const column of columns) {
    newValues[column.publicId] = values[column.publicId] ? values[column.publicId] : null
  }

  return newValues
}

const FormComponent: React.FC = () => {
  const themed = useThemedColours()
  const { project } = useProject()
  const {
    spreadsheetData,
    setSpreadsheetData,
    uniqueNumber,
    selectedCell,
    setSelectedCell,
    handleCreateRow,
    handleKeyDownForm,
    setIsFocused
  } = useDataContext()

  const [editColumnMenu, setEditColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [deleteColumnModal, setDeleteColumnModal] = useState<boolean>(false)
  const [changeColumnMenu, setChangeColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [cellMenu, setCellMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [currentViewId, setCurrentViewId] = useState<string>(spreadsheetData.viewDetails.publicId)
  const controllerRef = useRef<AbortController | null>(null)
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const descriptionTimeout = useRef<TimeoutID | null>(null)

  // The columns we use to build the form
  const columns = spreadsheetData.viewDetails.columns.filter(
    (column) =>
      !spreadsheetData.userConfiguration.hiddenColumns.find((hiddenColumnId) => hiddenColumnId === column.publicId)
  )

  const [formValues, setFormValues] = useState(() => createFormValueState({}, columns))

  // If column info is changed, update the form's values
  // Use functional update to avoid stale closure - formValues must come from callback parameter
  useEffect(() => {
    setFormValues((currentFormValues) => createFormValueState(currentFormValues, columns))
  }, [columns])

  // If new view is selected
  useEffect(() => {
    if (currentViewId !== spreadsheetData.viewDetails.publicId) {
      setFormSubmitted(false)
      setFormValues(createFormValueState({}, columns))
      setCurrentViewId(spreadsheetData.viewDetails.publicId)
    }
  }, [spreadsheetData.viewDetails])

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, columnId: string) => {
    if (spreadsheetData.isAdmin) {
      event.dataTransfer.setData('columnId', columnId)
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (spreadsheetData.isAdmin) {
      event.preventDefault()
    }
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (spreadsheetData.isAdmin) {
      event.dataTransfer.dropEffect = 'move'
    }
  }

  const handleOnDrop = (event: React.DragEvent<HTMLDivElement>, columnNumber: number) => {
    if (spreadsheetData.isAdmin) {
      const draggedColumnId = event.dataTransfer.getData('columnId')
      if (draggedColumnId && draggedColumnId !== '')
        setSpreadsheetData({
          type: 'CHANGE_COLUMN_ORDER',
          columnId: draggedColumnId,
          newIndex: columnNumber
        })
    }
  }

  const setFieldValue = async (columnId: string, value: ICellValue) => {
    // make sure values are saved in the state before calling preview row
    setFormValues((existingFormValues) => {
      return { ...existingFormValues, [columnId]: value }
    })

    // check if any of the of the columns needs to display pre-calculated data e.g. script
    // TODO: add join to validation
    const previewColumns = columns.filter((col) => col.scriptEnabled || col.formulaEnabled || col.isJoined)
    if (previewColumns.length > 0) {
      // abort any call made to preview row if any has been initiated
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      const controller = new AbortController()
      controllerRef.current = controller
      const signal = controllerRef.current.signal
      try {
        const previewRowRequest = await api.previewRow(
          project.publicId,
          spreadsheetData.activeTableView,
          { ...formValues, [columnId]: value },
          signal
        )
        setFormValues((formValues) => {
          const newFormValues = { ...formValues }
          for (const column of previewColumns) {
            newFormValues[column.publicId] = previewRowRequest.data[column.publicId]
          }
          return newFormValues
        })
      } catch (e) {
        displayErrorMessage(e)
      }
    }
  }

  const handleOnValueChange = async (cells: IUpdateTableViewCell, onSuccess: () => void) => {
    await setFieldValue(cells.cells[0].columnId, cells.cells[0].value)
    onSuccess()
  }

  const isFieldReadOnly = (column: ITableViewColumn) =>
    !spreadsheetData.isContributor ||
    isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) ||
    column.isJoined ||
    column.isSynced ||
    column.scriptEnabled ||
    column.formulaEnabled

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)

    // We need to remove keys with  and values that are:
    // - Empty strings : Empty strings are used as default values for keys
    // - Script and joined values: They are calculated values in the BE
    const allowedColumnValues = columns
      .filter((column) => !(column.isJoined || column.scriptEnabled || column.formulaEnabled))
      .map((column) => column.publicId)
    const values = Object.keys(formValues).reduce(
      (acc, key) => {
        if (allowedColumnValues.includes(key) && formValues[key] !== '') {
          acc[key] = formValues[key]
        }
        return acc
      },
      {} as Record<string, ICellValue>
    )

    // Check that required fields have a value
    for (const column of columns) {
      if (
        allowedColumnValues.includes(column.publicId) &&
        column.required &&
        isFieldValueEmpty(column.kind, values[column.publicId])
      ) {
        setConfirmSubmit(false)
        setSubmitting(false)
        setSnackbarMessage({
          message: `'${column.name}' is required. Fill it in before you submit the form.`,
          status: 'error'
        })
        return
      }
    }

    // Check that values are valid
    for (const column of columns) {
      if (spreadsheetData && spreadsheetData.rows) {
        const validValue = isValidValue(
          formValues[column.publicId],
          null,
          spreadsheetData.columnValuesCount,
          column,
          formValues,
          spreadsheetData?.viewDetails.type,
          true
        )

        if (validValue.error) {
          if (validValue.warn) setSnackbarMessage({ status: 'warning', message: validValue.errorMessage })
          else {
            setConfirmSubmit(false)
            setSubmitting(false)
            setSnackbarMessage({ status: 'error', message: validValue.errorMessage })
            return
          }
        }
      }
    }

    try {
      await handleCreateRow(values, undefined, true)
      setFormValues(createFormValueState({}, columns))
      setFormSubmitted(true)
      setSelectedCell(INITIAL_SELECTED_CELL)
      setConfirmSubmit(false)
      setSnackbarMessage({ message: 'Successfully submitted', status: 'success' })
      setSubmitting(false)
    } catch (e) {
      displayErrorMessage(e)
      setConfirmSubmit(false)
      setSubmitting(false)
    }
  }

  const handleResetSubmit = async () => {
    setFormSubmitted(false)
    await refreshSelectOptions(setSpreadsheetData, spreadsheetData)
  }

  const handleViewUpdateSuccess = () => {
    setSnackbarMessage({ status: 'success', message: 'The view description was updated successfully!' })
  }

  const handleViewUpdateFailure = (error: any) => {
    displayErrorMessage(error)
  }

  const handleDescriptionChange = (description: EditorContent) => {
    if (descriptionTimeout.current !== null) cancelTimeout(descriptionTimeout.current)

    descriptionTimeout.current = requestTimeout(() => {
      updateView(
        'description',
        spreadsheetData,
        setSpreadsheetData,
        spreadsheetData.viewDetails.publicId,
        isEditorEmpty(description) ? null : description,
        project.publicId,
        handleViewUpdateSuccess,
        handleViewUpdateFailure
      )
    }, 1000)
  }

  const disable = spreadsheetData.userConfiguration.unsavedChanges || submitting

  const cellMenuColumn = cellMenu.open ? columns.find((column) => column.publicId === cellMenu.columnId) : undefined

  return (
    <div className="form-container w-[70%] my-10 mx-auto p-10 max-lg:w-full max-lg:m-2.5 max-lg:p-4 relative bg-surface rounded shadow">
      <div className="flex w-full truncate font-bold text-2xl h-[70px] leading-[70px]">
        {spreadsheetData.viewDetails.name}
      </div>

      {!formSubmitted && (
        <div className="grid gap-[30px] grid-cols-2">
          {(spreadsheetData.viewDetails.description || spreadsheetData.isAdmin) && (
            <div className={`${spreadsheetData.isAdmin ? 'border border-divider rounded' : ''} col-span-full`}>
              {spreadsheetData.isAdmin && (
                <div className="border-b border-divider w-full p-2.5 font-bold">Form Description</div>
              )}

              <Editor
                key={spreadsheetData.viewDetails.publicId}
                databaseDoc={spreadsheetData.viewDetails.description}
                readOnly={!spreadsheetData.isAdmin}
                editorId={spreadsheetData.viewDetails.publicId}
                onChange={(value) => handleDescriptionChange(value)}
                resources={[{ resource: 'document_table', publicId: spreadsheetData.tableDetails.publicId }]}
              />
            </div>
          )}
          {columns.map((column: ITableViewColumn, columnNumber: number) => {
            const selected = selectedCell.rowId === 'new-row-form' && selectedCell.columnId === column.publicId
            const descriptionKey = hashString(JSON.stringify(column.description?.content ?? ''))
            const headerColours = themed.header(
              column.headerBackgroundColor || project.defaultHeaderBackgroundColor,
              column.headerTextColor || project.defaultHeaderTextColor
            )
            const formWidth = column.formWidth || DEFAULT_FORM_COLUMN_WIDTH // Lets make default full for now (same as current behaviour)
            const isColumnLocked = isColumnLockedByRules(
              spreadsheetData.userConfiguration.rowLockSettings,
              column.publicId
            )
            return (
              <div
                data-testid="form-column"
                key={columnNumber}
                className="w-full border border-divider rounded"
                style={{
                  gridColumn: formWidth === 2 ? 'span 2' : 'span 1'
                }}
                onDragStart={(event) => handleDragStart(event, column.publicId)}
                onDragOver={handleDragOver}
                onDrop={(event) => handleOnDrop(event, columnNumber)}
                onDragEnter={handleDragEnter}
                draggable={spreadsheetData.isAdmin && !isColumnLocked}
              >
                <div className="flex items-center w-full relative rounded-t p-2.5 min-h-[50px]" style={headerColours}>
                  <IconSelector kind={column.kind} />
                  <div className="ml-2.5">{column.name}</div>
                  {spreadsheetData.isAdmin && !isColumnLocked && (
                    // A real button, so it's reachable by keyboard and announced
                    // by name. It keeps the header's themed text colour.
                    <Button
                      internalType="transparent"
                      className="ml-[15px] h-6 w-6 p-0 rounded text-current hover:text-current hover:bg-black/10"
                      aria-label="Column options"
                      onClick={(event: React.MouseEvent<HTMLElement>) =>
                        setEditColumnMenu({
                          open: true,
                          top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
                          left: `${event.currentTarget.getBoundingClientRect().left - 130}px`,
                          right: 'auto',
                          bottom: 'auto',
                          columnNumber: columnNumber,
                          columnId: column.publicId,
                          rowNumber: -1,
                          rowId: ''
                        })
                      }
                    >
                      <Option />
                    </Button>
                  )}

                  {isColumnLocked && (
                    <div className="cursor-pointer ml-[15px]">
                      <Lock />
                    </div>
                  )}

                  {column.isJoined && (
                    <div className="cursor-pointer ml-[15px]">
                      <Join />
                    </div>
                  )}

                  {/* account for legacy forms where a column could be both locked through a join and required */}
                  {!column.isJoined && column.required && (
                    <small className="text-red ml-auto absolute right-2.5 top-[-25px]">* Required Field</small>
                  )}
                </div>

                {column.description && (
                  <div
                    key={`form-field-description-${column.publicId}-${descriptionKey}`}
                    className="flex items-center text-xs gap-2.5 pt-0 pb-2.5 px-2.5"
                    style={headerColours}
                  >
                    <Info />
                    {getPlainTextDescription(column.description)}
                  </div>
                )}

                <div className="form-field-cell">
                  <Cell
                    key={`$new-row-form-column-${column.publicId}`}
                    value={formValues[column.publicId]}
                    rowNumber={-2000}
                    rowId="new-row-form"
                    prevRowId="new-row-form"
                    nextRowId="new-row-form"
                    columnNumber={columnNumber}
                    columnId={column.publicId}
                    nextColumnId={columnNumber <= columns.length - 2 ? columns[columnNumber + 1].publicId : undefined}
                    prevColumnId={columnNumber > 0 ? columns[columnNumber - 1].publicId : undefined}
                    setSelectedCell={setSelectedCell}
                    contextClick={setCellMenu}
                    kind={column.kind}
                    scriptEnabled={column.scriptEnabled}
                    formulaEnabled={column.formulaEnabled}
                    isJoined={column.isJoined}
                    formValues={formValues}
                    frozen={false}
                    rowHeight={spreadsheetData.viewDetails.rowHeight}
                    isContributor={spreadsheetData.isContributor}
                    isAdmin={spreadsheetData.isAdmin}
                    handleKeyDown={handleKeyDownForm}
                    uniqueNumber={uniqueNumber}
                    locked={isColumnLocked || column.isJoined || column.isSynced}
                    setCellValue={handleOnValueChange}
                    columnName={column.name}
                    selected={selected}
                    isExpanded={true}
                    editing={selected && selectedCell.editing}
                    tempValue={selected ? selectedCell.value : undefined}
                    setIsFocused={setIsFocused}
                    type={spreadsheetData.viewDetails.type}
                    colourSettings={spreadsheetData.userConfiguration.colourSettings}
                    columnValuesCount={spreadsheetData.columnValuesCount}
                    column={column}
                    startRowId={spreadsheetData.rows[0]?.publicId}
                    finalRowId={spreadsheetData.rows[spreadsheetData.rows.length - 1]?.publicId}
                    finalRowNumber={
                      spreadsheetData.visibleBitmap ? spreadsheetData.visibleCount : spreadsheetData.rows.length
                    }
                    startColumnId={spreadsheetData.viewDetails.columns[0]?.publicId}
                    finalColumnId={
                      spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
                    }
                    finalColumnNumber={spreadsheetData.viewDetails.columns.length}
                    allowComments={spreadsheetData.tableDetails.allowComments}
                    row={null}
                    className="flex items-center"
                  />
                </div>
              </div>
            )
          })}

          {spreadsheetData.isContributor && (
            <div className="font-medium truncate text-lg col-span-full">
              {!confirmSubmit && (
                <div>
                  <Button
                    disabled={spreadsheetData.userConfiguration.unsavedChanges}
                    onClick={() => setConfirmSubmit(true)}
                  >
                    Submit Form
                  </Button>
                  {spreadsheetData.userConfiguration.unsavedChanges ? (
                    <span className="text-red ml-4">Please save changes to current view first, then submit form</span>
                  ) : null}
                </div>
              )}

              {confirmSubmit && (
                <div>
                  <p className="truncate my-8 mx-0">Are you sure you would like to submit the form?</p>
                  <div className="flex">
                    <Button
                      internalType="danger"
                      disabled={disable}
                      onClick={() => {
                        if (submitting) return
                        setConfirmSubmit(false)
                      }}
                    >
                      No
                    </Button>
                    <Button
                      className="ml-[15px]"
                      internalType="accept"
                      disabled={disable}
                      onClick={handleSubmit}
                      isLoading={submitting}
                    >
                      Yes
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {formSubmitted && (
        <div className="font-medium text-lg truncate mt-[30px]">
          <p className="mt-0 mb-8 mx-0">Thank you for your submission.</p>
          <Button data-testid="form-submit-button" onClick={handleResetSubmit}>
            Submit Another Form
          </Button>
        </div>
      )}

      <ColumnMenu
        id={`column-context-menu-${uniqueNumber}`}
        menuState={editColumnMenu}
        setMenuState={setEditColumnMenu}
        width={200}
        setDeleteColumnModal={setDeleteColumnModal}
        setEditColumnMenu={(open: boolean, panel?: 'formula') => {
          setEditColumnMenu(INITIAL_CONTEXT_MENU_STATE)
          setChangeColumnMenu({
            open,
            top: editColumnMenu.top,
            left: editColumnMenu.left,
            right: editColumnMenu.right,
            bottom: editColumnMenu.bottom,
            columnNumber: editColumnMenu.columnNumber,
            columnId: editColumnMenu.columnId,
            columnPanel: panel
          })
        }}
      />

      {cellMenuColumn && (
        <FormCellMenu
          id={`form-cell-menu-${uniqueNumber}`}
          menuState={cellMenu}
          setMenuState={setCellMenu}
          width={200}
          column={cellMenuColumn}
          value={formValues[cellMenuColumn.publicId]}
          formValues={formValues}
          readOnly={isFieldReadOnly(cellMenuColumn)}
          onChange={(value) => void setFieldValue(cellMenuColumn.publicId, value)}
        />
      )}

      {changeColumnMenu.open && (
        <EditColumnMenu
          id={`change-column-context-menu-${uniqueNumber}`}
          menuState={changeColumnMenu}
          setMenuState={setChangeColumnMenu}
        />
      )}

      {deleteColumnModal && (
        <DeleteColumnModal
          id="delete-column-modal"
          open={deleteColumnModal}
          setOpen={setDeleteColumnModal}
          columnIds={editColumnMenu.columnId ? [editColumnMenu.columnId] : []}
        />
      )}
    </div>
  )
}

export default FormComponent
